import { supabase } from "@/platform/lib/supabase";

export interface SessionPoint {
  id: string;
  kind: "diagnosis" | "simulation" | "study" | "review" | "simulation_full";
  ended_at: string;
  raw_score: number | null;
  by_discipline: Record<string, { total: number; correct: number }> | null;
  duration_seconds: number | null;
}

export interface DailyActivity {
  date: string; // YYYY-MM-DD
  attempts: number;
}

export interface ProgressOverview {
  totalSessions: number;
  totalAttempts: number;
  overallAccuracy: number; // 0..1
  sessions: SessionPoint[];
  /** Última snapshot disciplina → acurácia */
  latestByDiscipline: Array<{ discipline: string; accuracy: number; total: number; correct: number }>;
  activity30d: DailyActivity[];
  /**
   * Discursivas/redações corrigidas avulsas (fora de uma sessão de simulado).
   * Sem isto, o trabalho da aluna em /redacao e /discursivas fica invisível.
   */
  discursivePractice: Array<{ discipline: string; attempts: number; avgScore: number }>;
}

export async function fetchProgressOverview(userId: string): Promise<ProgressOverview> {
  // Sessões concluídas
  const { data: sessions, error: sErr } = await supabase
    .from("einstein_sessions")
    .select("id, kind, ended_at, raw_score, meta, duration_seconds")
    .eq("user_id", userId)
    .not("ended_at", "is", null)
    .order("ended_at", { ascending: true });
  if (sErr) throw sErr;

  type RawSession = {
    id: string;
    kind: SessionPoint["kind"];
    ended_at: string;
    raw_score: number | null;
    meta: { by_discipline?: SessionPoint["by_discipline"] } | null;
    duration_seconds: number | null;
  };
  const sessionPoints: SessionPoint[] = (sessions as RawSession[] | null ?? []).map((s) => ({
    id: s.id,
    kind: s.kind,
    ended_at: s.ended_at,
    raw_score: s.raw_score,
    by_discipline: s.meta?.by_discipline ?? null,
    duration_seconds: s.duration_seconds,
  }));

  // Total de attempts + acurácia geral
  const { count: totalAttempts } = await supabase
    .from("einstein_attempts")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId);

  const { count: correctAttempts } = await supabase
    .from("einstein_attempts")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId)
    .eq("is_correct", true);

  const overallAccuracy = (totalAttempts ?? 0) > 0 ? (correctAttempts ?? 0) / (totalAttempts ?? 1) : 0;

  // Última snapshot por disciplina = consolidando por todas as sessions
  const aggByDisc: Record<string, { total: number; correct: number }> = {};
  for (const s of sessionPoints) {
    if (!s.by_discipline) continue;
    for (const [d, v] of Object.entries(s.by_discipline)) {
      const bucket = aggByDisc[d] ?? { total: 0, correct: 0 };
      bucket.total += v.total;
      bucket.correct += v.correct;
      aggByDisc[d] = bucket;
    }
  }
  const latestByDiscipline = Object.entries(aggByDisc)
    .map(([discipline, v]) => ({
      discipline,
      total: v.total,
      correct: v.correct,
      accuracy: v.total > 0 ? v.correct / v.total : 0,
    }))
    .sort((a, b) => a.accuracy - b.accuracy);

  // Atividade últimos 30 dias
  const since = new Date(Date.now() - 30 * 86400000).toISOString();
  const { data: recent } = await supabase
    .from("einstein_attempts")
    .select("created_at")
    .eq("user_id", userId)
    .gte("created_at", since);

  const dayMap = new Map<string, number>();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
    dayMap.set(d, 0);
  }
  for (const a of (recent ?? []) as Array<{ created_at: string }>) {
    const day = a.created_at.slice(0, 10);
    if (dayMap.has(day)) dayMap.set(day, (dayMap.get(day) ?? 0) + 1);
  }
  const activity30d: DailyActivity[] = Array.from(dayMap.entries()).map(([date, attempts]) => ({
    date,
    attempts,
  }));

  // Discursivas/redações avulsas (attempts sem session_id, kind=discursive).
  const { data: discRows } = await supabase
    .from("einstein_attempts")
    .select("score, einstein_questions!inner(discipline, kind)")
    .eq("user_id", userId)
    .is("session_id", null)
    .eq("einstein_questions.kind", "discursive");

  const discMap = new Map<string, { attempts: number; scoreSum: number; scored: number }>();
  for (const r of (discRows ?? []) as unknown as Array<{
    score: number | null;
    einstein_questions: { discipline: string; kind: string } | { discipline: string; kind: string }[] | null;
  }>) {
    const q = Array.isArray(r.einstein_questions) ? r.einstein_questions[0] : r.einstein_questions;
    const disc = q?.discipline;
    if (!disc) continue;
    const b = discMap.get(disc) ?? { attempts: 0, scoreSum: 0, scored: 0 };
    b.attempts += 1;
    if (typeof r.score === "number") {
      b.scoreSum += r.score;
      b.scored += 1;
    }
    discMap.set(disc, b);
  }
  const discursivePractice = Array.from(discMap.entries())
    .map(([discipline, b]) => ({
      discipline,
      attempts: b.attempts,
      avgScore: b.scored > 0 ? Math.round((b.scoreSum / b.scored) * 10) / 10 : 0,
    }))
    .sort((a, b) => a.avgScore - b.avgScore);

  return {
    totalSessions: sessionPoints.length,
    totalAttempts: totalAttempts ?? 0,
    overallAccuracy,
    sessions: sessionPoints,
    latestByDiscipline,
    activity30d,
    discursivePractice,
  };
}
